import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Tag, Check, Copy, Calendar } from 'lucide-react';

const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112'
];

export const generateCode128Svg = (text, height = 36) => {
  const clean = String(text || '').trim();
  if (!clean) return '';
  const START_B = 104;
  const STOP = 106;
  const codes = [START_B];
  let checkSum = START_B;
  for (let i = 0; i < clean.length; i++) {
    const code = clean.charCodeAt(i) - 32;
    if (code < 0 || code > 95) continue;
    codes.push(code);
    checkSum += code * (i + 1);
  }
  codes.push(checkSum % 103);
  codes.push(STOP);

  let x = 10;
  const rects = [];
  codes.forEach((c) => {
    const p = CODE128_PATTERNS[c];
    if (!p) return;
    for (let i = 0; i < p.length; i++) {
      const w = parseInt(p[i], 10);
      if (i % 2 === 0) {
        rects.push(`<rect x="${x}" y="0" width="${w}" height="${height}" fill="#000" />`);
      }
      x += w;
    }
  });
  x += 10;
  return `<svg viewBox="0 0 ${x} ${height}" width="100%" height="${height}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style="display:block;">${rects.join('')}</svg>`;
};

export const getTodayBatchCode = () => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
};

export const formatBatchNumber = (val) => {
  if (!val) return '';
  let clean = String(val).replace(/^(batch|b)[-:\s]*/i, '').trim();
  // Strip '20' from 8-digit date 20YYMMDD -> 6-digit YYMMDD (e.g. 20261023 -> 261023)
  clean = clean.replace(/^20(\d{6})/i, '$1');
  return clean || '';
};

export const StickyLabelPrintModal = ({
  isOpen,
  onClose,
  item = {},
  orgName = 'MITRA POS',
  branchName = 'Main Store',
}) => {
  const [qty, setQty] = useState(1);
  const [labelSize, setLabelSize] = useState('50x30'); // '50x30' or '40x25'
  
  const [prodName, setProdName] = useState(item.name || 'Product Item');
  const [variant, setVariant] = useState(item.label || '');
  const [price, setPrice] = useState(item.sellPrice ?? item.price ?? 0);
  const [batch, setBatch] = useState(item.batchNumber ? formatBatchNumber(item.batchNumber) : '');
  const [expiry, setExpiry] = useState(item.expiryDate || '');
  const [barcode, setBarcode] = useState(item.barcode || '200001');

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePrint = () => {
    // Inject styles and trigger print
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) {
      window.print();
      return;
    }

    const isCompact = labelSize === '40x25';
    const barcodeSvg = generateCode128Svg(barcode || '200001', isCompact ? 22 : 32);

    const labelsHtml = Array.from({ length: Number(qty) || 1 }).map(() => `
      <div class="label-page">
        <div class="label-box">
          <div class="prod-name">${prodName.toUpperCase()}</div>
          ${variant ? `<div class="variant-tag">${variant.toUpperCase()} • ₹${price}</div>` : (price > 0 ? `<div class="variant-tag">₹${price}</div>` : '')}
          <div class="meta-row">
            <span>Batch: ${formatBatchNumber(batch)}</span>
            <span>Exp: ${expiry || '—'}</span>
          </div>
          <div class="barcode-wrap">${barcodeSvg}</div>
          <div class="barcode-num">${barcode || '—'}</div>
        </div>
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Sticky Labels - ${prodName}</title>
        <style>
          @page {
            size: ${isCompact ? '40mm 25mm' : '50mm 30mm'};
            margin: 0;
          }
          * { 
            box-sizing: border-box; 
            margin: 0; 
            padding: 0; 
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { background: white; margin: 0; padding: 0; }
          .label-page {
            width: ${isCompact ? '40mm' : '50mm'};
            height: ${isCompact ? '25mm' : '30mm'};
            page-break-after: always;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .label-box {
            width: 100%;
            height: 100%;
            padding: ${isCompact ? '1mm 1.5mm' : '2mm 2.5mm'};
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            text-align: center;
            border: 1px dashed #ccc;
          }
          @media print {
            .label-box { border: none; }
          }
          .prod-name { font-size: ${isCompact ? '6.5pt' : '8pt'}; font-weight: 900; line-height: 1.1; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .variant-tag { display: block; margin: 0.3mm auto 0; font-size: ${isCompact ? '5.5pt' : '6.5pt'}; font-weight: 700; text-transform: uppercase; color: #111; line-height: 1.2; }
          .meta-row { display: flex; justify-content: space-between; font-size: ${isCompact ? '4.5pt' : '6pt'}; font-weight: 600; color: #222; margin: 0.2mm 0; }
          .price-val { font-size: ${isCompact ? '8.5pt' : '11pt'}; font-weight: 900; line-height: 1; }
          .barcode-wrap {
            width: 90%;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .barcode-wrap svg {
            width: 100%;
            max-width: ${isCompact ? '32mm' : '42mm'};
            height: ${isCompact ? '4.5mm' : '6.5mm'};
          }
          .barcode-num { font-family: monospace; font-size: ${isCompact ? '5.5pt' : '7.5pt'}; font-weight: 700; letter-spacing: 1px; line-height: 1; }
        </style>
      </head>
      <body>
        ${labelsHtml}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Tag size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Thermal Sticky Barcode Label Printer</h3>
              <p className="text-xs text-slate-500">Print adhesive barcode labels for loose items, produce, or bakery</p>
            </div>
          </div>
          <button 
            type="button" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose?.();
            }}
            aria-label="Close"
            className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
          >
            <X size={18} className="pointer-events-none" />
          </button>
        </header>

        {/* Content Body */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto custom-scrollbar">
          
          {/* Controls */}
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                Label Roll Size
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setLabelSize('50x30')}
                  className={`px-3 py-2 text-xs font-bold rounded-lg border text-center transition-all ${
                    labelSize === '50x30' 
                      ? 'border-blue-600 bg-blue-50/60 text-blue-700 shadow-sm' 
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  50mm × 30mm (Standard)
                </button>
                <button
                  type="button"
                  onClick={() => setLabelSize('40x25')}
                  className={`px-3 py-2 text-xs font-bold rounded-lg border text-center transition-all ${
                    labelSize === '40x25' 
                      ? 'border-blue-600 bg-blue-50/60 text-blue-700 shadow-sm' 
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  40mm × 25mm (Compact)
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                Number of Stickers to Print
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 focus:outline-none focus:border-blue-500"
                />
                <span className="text-xs text-slate-500 whitespace-nowrap font-medium">labels</span>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-3">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">
                Label Content Adjustments
              </span>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-bold text-slate-900"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Barcode</label>
                  <input
                    type="text"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-xs font-mono font-bold text-slate-900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Batch No</label>
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      value={batch}
                      onChange={(e) => setBatch(e.target.value)}
                      placeholder="e.g. 261027"
                      className="w-full px-2.5 pr-18 py-1.5 border border-slate-200 rounded-md text-xs font-mono text-slate-900 focus:outline-none focus:border-slate-400"
                    />
                    <button
                      type="button"
                      onClick={() => setBatch(getTodayBatchCode())}
                      className="absolute right-1 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded flex items-center gap-0.5 transition-all shadow-2xs active:scale-95 cursor-pointer"
                      title="Set batch number to today (YYMMDD format)"
                    >
                      <Calendar size={10} className="text-slate-500" />
                      <span>TODAY</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Expiry Date</label>
                  <input
                    type="date"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-xs text-slate-900"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Live Preview */}
          <div className="flex flex-col items-center justify-center p-4 bg-slate-50/70 border border-dashed border-slate-200 rounded-xl">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3 block">
              Physical Thermal Sticker Preview ({labelSize === '50x30' ? '50mm × 30mm' : '40mm × 25mm'})
            </span>

            {/* Sticker Mockup */}
            <div 
              style={{
                width: labelSize === '50x30' ? '220px' : '204px',
                height: labelSize === '50x30' ? '132px' : '128px',
              }}
              className={`bg-white rounded shadow-md border border-slate-300 flex flex-col justify-between text-center select-none overflow-hidden ${
                labelSize === '40x25' ? 'p-2' : 'p-2.5'
              }`}
            >
              <div>
                <div className={`font-black uppercase tracking-wide text-slate-950 line-clamp-1 leading-tight ${
                  labelSize === '40x25' ? 'text-[11px]' : 'text-[12.5px]'
                }`}>
                  {prodName.toUpperCase()}
                </div>
                {variant ? (
                  <div className={`font-bold text-slate-700 mt-0.5 ${
                    labelSize === '40x25' ? 'text-[8.5px]' : 'text-[9.5px]'
                  }`}>
                    {variant} • ₹{price}
                  </div>
                ) : price > 0 ? (
                  <div className={`font-bold text-slate-700 mt-0.5 ${
                    labelSize === '40x25' ? 'text-[8.5px]' : 'text-[9.5px]'
                  }`}>
                    ₹{price}
                  </div>
                ) : null}
              </div>

              <div className={`flex items-center justify-between font-semibold text-slate-600 px-1 ${
                labelSize === '40x25' ? 'text-[7.5px]' : 'text-[8px]'
              }`}>
                <span>Batch: {batch ? formatBatchNumber(batch) : '—'}</span>
                <span>Exp: {expiry || '—'}</span>
              </div>

              {/* Barcode Graphic */}
              <div>
                <div 
                  className={`w-4/5 mx-auto flex items-center justify-center overflow-hidden ${
                    labelSize === '40x25' ? 'h-5' : 'h-6'
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: generateCode128Svg(barcode || '200001', labelSize === '40x25' ? 20 : 26),
                  }}
                />
                <div className={`font-mono font-bold tracking-widest text-slate-900 ${
                  labelSize === '40x25' ? 'text-[7.5px] mt-0' : 'text-[9px] mt-0.5'
                }`}>
                  {barcode || '—'}
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 text-center mt-3 max-w-[220px] leading-tight">
              Pre-formatted for standard thermal barcode sticker printers (TSC, Zebra, Xprinter, TVS).
            </p>
          </div>

        </div>

        {/* Footer */}
        <footer className="px-6 py-3.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="text-xs text-slate-500 font-medium">
            Total to Print: <strong className="text-slate-900 font-bold">{qty} stickers</strong>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose?.();
              }}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-2 transition-all active:scale-[0.98]"
            >
              <Printer size={15} />
              Print
            </button>
          </div>
        </footer>

      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};
